/**
 * Central CRUD for clients, trips, notes.
 * localStorage is the primary store; writes are also pushed to the backend.
 */

import { apiPut, apiGet, getUserEmailFromToken } from './apiClient';
import type {
  Client,
  ClientNote,
  Trip,
  TripType,
  TravelCompanion,
  LoyaltyProgramEntry,
  ItineraryDay,
  ItineraryItem,
  Address,
  PassportInfo,
  DriverLicenseInfo,
  TripPreferences,
  PreferredContactMethod,
  AirClass,
  TravelStyle,
} from '../types/client';
import {
  createEmptyClient,
  createEmptyAddress,
  createEmptyTrip,
  createEmptyTripPreferences,
  createEmptyTravelCompanion,
} from '../types/client';

const STORAGE_PREFIX = 'wt_clients_';

function getStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function loadClients(userId: string): Client[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      const legacy = parsed && typeof parsed === 'object' && parsed.clients;
      if (Array.isArray(legacy)) return legacy;
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

const BACKEND_KEY_PREFIX = 'wt_clients';

function saveClients(userId: string, clients: Client[]): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(clients));
  // Fire-and-forget push to backend
  pushToBackend(clients);
}

/** Push clients data to the backend. Fire-and-forget; errors are logged. */
function pushToBackend(clients: Client[]): void {
  const email = getUserEmailFromToken();
  if (!email) return;
  const key = `${BACKEND_KEY_PREFIX}-${email}`;
  apiPut(key, key, { clients }).catch((err) => {
    console.warn('clientsService: backend push failed', err);
  });
}

/**
 * Push current localStorage clients data to the backend.
 * Call on page mount to ensure existing data is synced.
 */
export function pushClientsToBackend(userId: string): void {
  const clients = loadClients(userId);
  if (clients.length === 0) return;
  pushToBackend(clients);
}

/**
 * Pull clients from the backend and write to localStorage (no push).
 * Call on login so user continues where they left off.
 * If apiGet returns null or body has no clients, does not write.
 */
export async function pullClientsFromBackend(userId: string, email: string): Promise<void> {
  try {
    const key = `${BACKEND_KEY_PREFIX}-${email}`;
    const body = await apiGet<{ clients?: unknown[] }>(key);
    if (!body || !body.clients || !Array.isArray(body.clients) || body.clients.length === 0) return;
    const clients: Client[] = body.clients.map((c) => {
      const migrated = isLegacyClient(c) ? migrateLegacyClient(c) : (c as Client);
      return normalizeClient(migrated);
    });
    localStorage.setItem(getStorageKey(userId), JSON.stringify(clients));
  } catch (err) {
    console.warn('clientsService: pull from backend failed', err);
  }
}

function parseAddress(v: unknown): Address {
  const empty = createEmptyAddress();
  if (!v || typeof v !== 'object' || Array.isArray(v)) return empty;
  const o = v as Record<string, unknown>;
  return {
    line1: String(o.line1 ?? empty.line1),
    line2: String(o.line2 ?? empty.line2),
    city: String(o.city ?? empty.city),
    state: String(o.state ?? empty.state),
    zip: String(o.zip ?? empty.zip),
    country: String(o.country ?? empty.country),
  };
}

function parseAddressFromString(s: string | undefined): Address {
  const empty = createEmptyAddress();
  if (!s || typeof s !== 'string') return empty;
  return { ...empty, line1: s };
}

function parsePassport(v: unknown): PassportInfo | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  return {
    number: String(o.number ?? ''),
    countryOfIssue: String(o.countryOfIssue ?? ''),
    dateOfIssue: typeof o.dateOfIssue === 'string' ? o.dateOfIssue : null,
    dateOfExpiry: typeof o.dateOfExpiry === 'string' ? o.dateOfExpiry : null,
    placeOfBirth: String(o.placeOfBirth ?? ''),
  };
}

function parseDriverLicense(v: unknown): DriverLicenseInfo | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  return {
    number: String(o.number ?? ''),
    state: String(o.state ?? ''),
    expiration: typeof o.expiration === 'string' ? o.expiration : null,
  };
}

function parseChildrenAges(v: unknown): number[] {
  if (Array.isArray(v)) {
    return v.map((n) => (typeof n === 'number' && !Number.isNaN(n) ? n : 0)).filter((n) => n > 0);
  }
  if (typeof v === 'string') {
    return v
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
  }
  if (typeof v === 'number' && !Number.isNaN(v) && v > 0) return [v];
  return [];
}

function parseLoyaltyProgramEntry(o: unknown): LoyaltyProgramEntry {
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return { id: crypto.randomUUID(), program: '', number: '', preferred: false };
  }
  const obj = o as Record<string, unknown>;
  return {
    id: typeof obj.id === 'string' ? obj.id : crypto.randomUUID(),
    program: String(obj.program ?? ''),
    number: String(obj.number ?? ''),
    preferred: Boolean(obj.preferred),
  };
}

function parseLoyaltyPrograms(v: unknown): LoyaltyProgramEntry[] {
  if (Array.isArray(v)) {
    return v.map((x) => {
      if (x != null && typeof x === 'object' && !Array.isArray(x)) return parseLoyaltyProgramEntry(x);
      if (typeof x === 'string') return { id: crypto.randomUUID(), program: x, number: '', preferred: false };
      return parseLoyaltyProgramEntry(null);
    });
  }
  if (typeof v === 'string') {
    return v.split('\n').map((s) => s.trim()).filter(Boolean).map((s) => ({ id: crypto.randomUUID(), program: s, number: '', preferred: false }));
  }
  return [];
}

function parseCompanionLoyaltyPrograms(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string').map(String);
  if (typeof v === 'string') return v.split('\n').map((s) => s.trim()).filter(Boolean);
  return [];
}

function parseNotes(v: unknown): ClientNote[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((n): n is ClientNote => n != null && typeof n === 'object' && typeof (n as ClientNote).text === 'string')
    .map((n) => ({
      id: typeof (n as ClientNote).id === 'string' ? (n as ClientNote).id : crypto.randomUUID(),
      text: String((n as ClientNote).text),
      createdAt:
        typeof (n as ClientNote).createdAt === 'string'
          ? (n as ClientNote).createdAt
          : new Date().toISOString(),
    }));
}

function parseTravelCompanion(v: unknown): TravelCompanion {
  const empty = createEmptyTravelCompanion();
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    return { ...empty, id: crypto.randomUUID() };
  }
  const o = v as Record<string, unknown>;
  const p = o.passport;
  const d = o.driversLicense;
  return {
    id: typeof o.id === 'string' ? o.id : crypto.randomUUID(),
    firstName: String(o.firstName ?? empty.firstName),
    lastName: String(o.lastName ?? empty.lastName),
    relationship: ['spouse', 'child', 'parent', 'sibling', 'friend', 'coworker', 'other'].includes(
      String(o.relationship)
    )
      ? (o.relationship as TravelCompanion['relationship'])
      : 'other',
    age: typeof o.age === 'number' && !Number.isNaN(o.age) ? o.age : null,
    passport: p ? parsePassport(p) : null,
    driversLicense: d ? parseDriverLicense(d) : null,
    loyaltyPrograms: parseCompanionLoyaltyPrograms(o.loyaltyPrograms),
  };
}

function parseItineraryItemLocation(v: unknown): ItineraryItem['location'] | undefined {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const loc = v as Record<string, unknown>;
    const address = typeof loc.address === 'string' ? loc.address.trim() : '';
    if (!address) return undefined;
    const lat = typeof loc.latitude === 'number' && Number.isFinite(loc.latitude) ? loc.latitude : undefined;
    const lng = typeof loc.longitude === 'number' && Number.isFinite(loc.longitude) ? loc.longitude : undefined;
    const hasCoords =
      lat != null &&
      lng != null &&
      !(lat === 0 && lng === 0);
    return hasCoords
      ? { address, latitude: lat, longitude: lng }
      : { address };
  }
  return undefined;
}

function parseItineraryItem(v: unknown): ItineraryItem {
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    return { id: crypto.randomUUID(), title: '', type: 'other' };
  }
  const o = v as Record<string, unknown>;
  const validTypes = ['activity', 'meal', 'accommodation', 'transport', 'note', 'other'];
  const durationMinutes = typeof o.durationMinutes === 'number' && o.durationMinutes >= 0 ? o.durationMinutes : undefined;
  const checkIn = typeof o.accommodationCheckInDate === 'string' ? o.accommodationCheckInDate : undefined;
  const checkOut = typeof o.accommodationCheckOutDate === 'string' ? o.accommodationCheckOutDate : undefined;
  const legacyAddress = typeof o.address === 'string' ? o.address.trim() : '';
  const location =
    parseItineraryItemLocation(o.location) ??
    (legacyAddress ? { address: legacyAddress } : undefined);
  return {
    id: typeof o.id === 'string' ? o.id : crypto.randomUUID(),
    title: String(o.title ?? ''),
    type: validTypes.includes(String(o.type)) ? (o.type as ItineraryItem['type']) : 'other',
    time: typeof o.time === 'string' ? o.time : undefined,
    durationMinutes,
    notes: typeof o.notes === 'string' ? o.notes : undefined,
    url: typeof o.url === 'string' ? o.url : undefined,
    phone: typeof o.phone === 'string' ? o.phone : undefined,
    // Prefer nested location; keep flat address only when no location was derived
    address: location ? undefined : legacyAddress || undefined,
    location,
    activityId: typeof o.activityId === 'string' ? o.activityId : undefined,
    mealId: typeof o.mealId === 'string' ? o.mealId : undefined,
    hotelId: typeof o.hotelId === 'string' ? o.hotelId : undefined,
    accommodationCheckInDate: checkIn,
    accommodationCheckOutDate: checkOut,
    confirmationNumber: typeof o.confirmationNumber === 'string' ? o.confirmationNumber : undefined,
  };
}

function parseItineraryDays(v: unknown): ItineraryDay[] {
  if (!Array.isArray(v)) return [];
  return v.map((d) => {
    if (!d || typeof d !== 'object' || Array.isArray(d)) return { date: '', items: [] };
    const o = d as Record<string, unknown>;
    const items = Array.isArray(o.items)
      ? (o.items as unknown[]).map(parseItineraryItem)
      : [];
    const cityId = typeof o.cityId === 'string' && o.cityId ? o.cityId : undefined;
    return {
      date: String(o.date ?? ''),
      items,
      ...(cityId ? { cityId } : {}),
    };
  });
}

const VALID_FOOD = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'kosher', 'halal', 'low-carb', 'pescatarian', 'none'];
const VALID_ACTIVITIES = ['museums', 'outdoor', 'cultural', 'food', 'shopping', 'nightlife', 'adventure', 'relaxation', 'history', 'art'];
const VALID_ACCOMMODATION = ['boutique', 'luxury', 'budget', 'chain', 'resort', 'home-rental'];

function parseTripPreferences(v: unknown): TripPreferences {
  const empty = createEmptyTripPreferences();
  if (!v || typeof v !== 'object' || Array.isArray(v)) return empty;
  const o = v as Record<string, unknown>;
  return {
    airClass: ['any', 'business', 'first'].includes(String(o.airClass)) ? (o.airClass as AirClass) : 'any',
    food: Array.isArray(o.food) ? o.food.filter((x) => VALID_FOOD.includes(String(x))) as TripPreferences['food'] : [],
    activityPreferences: Array.isArray(o.activityPreferences)
      ? o.activityPreferences.filter((x) => VALID_ACTIVITIES.includes(String(x))) as TripPreferences['activityPreferences']
      : [],
    accommodationType: Array.isArray(o.accommodationType)
      ? o.accommodationType.filter((x) => VALID_ACCOMMODATION.includes(String(x))) as TripPreferences['accommodationType']
      : [],
    accommodationBudgetPerNight:
      typeof o.accommodationBudgetPerNight === 'number' && !Number.isNaN(o.accommodationBudgetPerNight)
        ? o.accommodationBudgetPerNight
        : null,
    travelStyle: ['relaxed', 'moderate', 'fast-paced'].includes(String(o.travelStyle))
      ? (o.travelStyle as TravelStyle)
      : 'moderate',
  };
}

function parseTrip(v: unknown): Trip {
  const empty = createEmptyTrip();
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    return { ...empty, id: crypto.randomUUID() };
  }
  const o = v as Record<string, unknown>;
  const companions = Array.isArray(o.travelCompanions)
    ? (o.travelCompanions as unknown[]).map(parseTravelCompanion)
    : [];
  const cityIds = Array.isArray(o.cityIds)
    ? (o.cityIds as unknown[]).filter((x): x is string => typeof x === 'string')
    : undefined;
  const cityHistories = parseCityHistories(o.cityHistories);
  const tripType = typeof o.tripType === 'string' ? (o.tripType as TripType) : null;
  return {
    id: typeof o.id === 'string' ? o.id : crypto.randomUUID(),
    title: String(o.title ?? empty.title),
    tripType,
    info: String(o.info ?? empty.info),
    startDate: typeof o.startDate === 'string' ? o.startDate : null,
    endDate: typeof o.endDate === 'string' ? o.endDate : null,
    cityIds: cityIds && cityIds.length > 0 ? cityIds : undefined,
    cityHistories: cityHistories && Object.keys(cityHistories).length > 0 ? cityHistories : undefined,
    groupSize: typeof o.groupSize === 'number' ? o.groupSize : null,
    adults: typeof o.adults === 'number' ? o.adults : null,
    itineraryDays: parseItineraryDays(o.itineraryDays),
    children: typeof o.children === 'number' ? o.children : null,
    childrenAges: parseChildrenAges(o.childrenAges),
    preferences: parseTripPreferences(o.preferences),
    travelCompanions: companions,
  };
}

function parseCityHistories(v: unknown): Record<string, { summary: string }> | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const o = v as Record<string, unknown>;
  const result: Record<string, { summary: string }> = {};
  for (const key of Object.keys(o)) {
    const entry = o[key];
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const summary = typeof (entry as Record<string, unknown>).summary === 'string'
        ? (entry as Record<string, unknown>).summary as string
        : '';
      result[key] = { summary };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function isLegacyClient(c: unknown): c is { name?: string; contact?: unknown } {
  return c != null && typeof c === 'object' && !Array.isArray(c) && !('firstName' in c);
}

function migrateLegacyClient(c: Client | Record<string, unknown>): Client {
  if (!isLegacyClient(c)) return normalizeClient(c as Client);

  const legacy = c as {
    name?: string;
    startDate?: string | null;
    endDate?: string | null;
    groupSize?: number | null;
    adults?: number | null;
    children?: number | null;
    childrenAges?: unknown;
    contact?: Record<string, unknown>;
    preferences?: Record<string, string>;
    notes?: unknown;
  };

  const nameParts = String(legacy.name ?? '').trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ') ?? '';

  const contact = legacy.contact && typeof legacy.contact === 'object' ? legacy.contact : {};
  const co = contact as Record<string, unknown>;

  const trips: Trip[] = [
    parseTrip({
      id: crypto.randomUUID(),
      title: '',
      info: '',
      startDate: legacy.startDate,
      endDate: legacy.endDate,
      groupSize: legacy.groupSize,
      adults: legacy.adults,
      children: legacy.children,
      childrenAges: legacy.childrenAges,
      preferences: legacy.preferences ?? {},
      travelCompanions: [],
    }),
  ];

  return {
    id: typeof (c as Record<string, unknown>).id === 'string' ? (c as Record<string, unknown>).id as string : crypto.randomUUID(),
    firstName,
    lastName,
    mobilePhone: String(co.mobilePhone ?? ''),
    workPhone: String(co.workPhone ?? ''),
    homePhone: String(co.homePhone ?? ''),
    personalEmail: String(co.homeEmail ?? co.personalEmail ?? ''),
    workEmail: String(co.workEmail ?? ''),
    address: parseAddressFromString(co.homeAddress as string),
    workAddress: parseAddressFromString(co.workAddress as string),
    preferredContactMethod: ['email', 'phone', 'text'].includes(String(co.preferredContactMethod))
      ? (co.preferredContactMethod as PreferredContactMethod)
      : 'email',
    passport: null,
    driversLicense: null,
    loyaltyPrograms: [],
    trips,
    notes: parseNotes(legacy.notes),
  };
}

function normalizeClient(c: Client): Client {
  const trips = Array.isArray(c.trips) ? c.trips.map(parseTrip) : [parseTrip(createEmptyTrip())];
  if (trips.length === 0) trips.push(parseTrip(createEmptyTrip()));

  return {
    ...c,
    firstName: String(c.firstName ?? ''),
    lastName: String(c.lastName ?? ''),
    mobilePhone: String(c.mobilePhone ?? ''),
    workPhone: String(c.workPhone ?? ''),
    homePhone: String(c.homePhone ?? ''),
    personalEmail: String(c.personalEmail ?? ''),
    workEmail: String(c.workEmail ?? ''),
    address: parseAddress(c.address),
    workAddress: parseAddress(c.workAddress),
    preferredContactMethod: ['email', 'phone', 'text'].includes(String(c.preferredContactMethod))
      ? c.preferredContactMethod
      : 'email',
    passport: c.passport ? parsePassport(c.passport) : null,
    driversLicense: c.driversLicense ? parseDriverLicense(c.driversLicense) : null,
    loyaltyPrograms: parseLoyaltyPrograms(c.loyaltyPrograms),
    trips,
    notes: parseNotes(c.notes),
  };
}

export function getAllClients(userId: string): Client[] {
  const raw = loadClients(userId);
  if (!Array.isArray(raw)) return [];
  const normalized = raw.map((c) => {
    const migrated = isLegacyClient(c) ? migrateLegacyClient(c) : (c as Client);
    return normalizeClient(migrated);
  });
  if (normalized.length > 0 && JSON.stringify(raw) !== JSON.stringify(normalized)) {
    saveClients(userId, normalized);
  }
  return normalized;
}

export function getClient(userId: string, clientId: string): Client | null {
  const clients = loadClients(userId);
  const found = clients.find((c) => (c as Client).id === clientId) ?? null;
  if (!found) return null;
  const migrated = isLegacyClient(found) ? migrateLegacyClient(found) : (found as Client);
  return normalizeClient(migrated);
}

const normalizePhone = (v: string): string => v.replace(/\D/g, '').trim();
const normalizeEmail = (v: string): string => v.trim().toLowerCase();

export function findClientByMobilePhone(userId: string, mobilePhone: string, excludeClientId?: string): Client | null {
  const p = normalizePhone(mobilePhone);
  if (!p) return null;
  const clients = getAllClients(userId);
  return clients.find((c) => {
    if (excludeClientId && c.id === excludeClientId) return false;
    return normalizePhone(c.mobilePhone ?? '') === p;
  }) ?? null;
}

export function findClientByEmail(userId: string, email: string, excludeClientId?: string): Client | null {
  const e = normalizeEmail(email);
  if (!e) return null;
  const clients = getAllClients(userId);
  return clients.find((c) => {
    if (excludeClientId && c.id === excludeClientId) return false;
    const pe = normalizeEmail(c.personalEmail ?? '');
    const we = normalizeEmail(c.workEmail ?? '');
    return pe === e || we === e;
  }) ?? null;
}

export function createClient(userId: string, data: Omit<Client, 'id'>): Client {
  const clients = loadClients(userId);
  const client = normalizeClient({
    ...data,
    id: crypto.randomUUID(),
  } as Client);
  clients.push(client);
  saveClients(userId, clients);
  return client;
}

export function updateClient(
  userId: string,
  clientId: string,
  updates: Partial<Omit<Client, 'id'>>
): Client | null {
  const clients = loadClients(userId);
  const idx = clients.findIndex((c) => (c as Client).id === clientId);
  if (idx === -1) return null;
  const existing = isLegacyClient(clients[idx]) ? migrateLegacyClient(clients[idx]) : (clients[idx] as Client);
  clients[idx] = normalizeClient({ ...existing, ...updates });
  saveClients(userId, clients);
  return clients[idx];
}

export function deleteClient(userId: string, clientId: string): boolean {
  const clients = loadClients(userId);
  const filtered = clients.filter((c) => (c as Client).id !== clientId);
  if (filtered.length === clients.length) return false;
  saveClients(userId, filtered);
  return true;
}

// Trips
export function addTrip(userId: string, clientId: string, trip: Omit<Trip, 'id'>): Trip | null {
  const client = getClient(userId, clientId);
  if (!client) return null;
  const newTrip: Trip = { ...trip, id: crypto.randomUUID() };
  const trips = [...(Array.isArray(client.trips) ? client.trips : []), newTrip];
  const updated = updateClient(userId, clientId, { trips });
  return updated ? newTrip : null;
}

export function updateTrip(
  userId: string,
  clientId: string,
  tripId: string,
  updates: Partial<Omit<Trip, 'id'>>
): Trip | null {
  const client = getClient(userId, clientId);
  if (!client) return null;
  const trips = Array.isArray(client.trips) ? client.trips : [];
  const idx = trips.findIndex((t) => t.id === tripId);
  if (idx === -1) return null;
  trips[idx] = { ...trips[idx], ...updates };
  const updated = updateClient(userId, clientId, { trips });
  return updated ? trips[idx] : null;
}

export function deleteTrip(userId: string, clientId: string, tripId: string): boolean {
  const client = getClient(userId, clientId);
  if (!client) return false;
  const trips = (Array.isArray(client.trips) ? client.trips : []).filter((t) => t.id !== tripId);
  if (trips.length === (client.trips?.length ?? 0)) return false;
  updateClient(userId, clientId, { trips });
  return true;
}

// Notes
export function addNote(userId: string, clientId: string, text: string): ClientNote | null {
  const client = getClient(userId, clientId);
  if (!client) return null;
  const note: ClientNote = {
    id: crypto.randomUUID(),
    text,
    createdAt: new Date().toISOString(),
  };
  const notes = [...(Array.isArray(client.notes) ? client.notes : []), note];
  updateClient(userId, clientId, { notes });
  return note;
}

export function updateNote(
  userId: string,
  clientId: string,
  noteId: string,
  text: string
): ClientNote | null {
  const client = getClient(userId, clientId);
  if (!client) return null;
  const notes = (Array.isArray(client.notes) ? client.notes : []).map((n) =>
    n.id === noteId ? { ...n, text } : n
  );
  updateClient(userId, clientId, { notes });
  return notes.find((n) => n.id === noteId) ?? null;
}

export function deleteNote(userId: string, clientId: string, noteId: string): boolean {
  const client = getClient(userId, clientId);
  if (!client) return false;
  const notes = (Array.isArray(client.notes) ? client.notes : []).filter((n) => n.id !== noteId);
  updateClient(userId, clientId, { notes });
  return true;
}

export { createEmptyClient, createEmptyTrip, createEmptyTravelCompanion };
